/**
 * Pick-time checks for files attached to a chat message, against the
 * server's file policy: its allowed types, its per-file size limit and its
 * per-message count. Nothing is uploaded here; accepted files are only staged.
 */
import {
  threadFileTypeFor,
  type ThreadFileKind,
  type ThreadFilePolicy,
  type ThreadFilePolicyType,
} from '@discovr/types';

import { formatBytes } from '../format';
import { COMPRESSIBLE_KIND, COMPRESSIBLE_PICK_SIZE_FACTOR } from './compressImage';
import { createObjectUrl } from './objectUrl';

/** Built from the policy's limit, so the copy always names the size the check enforces. */
export const FILE_TOO_LARGE_MESSAGE = (name: string, maxBytes: number): string => `"${name}" is larger than ${formatBytes(maxBytes)}.`;
export const FILE_TYPE_REFUSED_MESSAGE = (name: string): string =>
  `"${name}" can't be attached. You can attach images, videos, PDFs, and Word, Excel or PowerPoint files.`;
/** The server rejects an empty file late, with a confusing type-mismatch reason, so it is refused here. */
export const FILE_EMPTY_MESSAGE = (name: string): string => `"${name}" is empty.`;
/** Built from the policy's cap, so the copy always names the count the check enforces. */
export const TOO_MANY_FILES_MESSAGE = (maxFiles: number): string => `You can attach up to ${maxFiles} files to a message.`;

const PREVIEWABLE_KIND: ThreadFileKind = 'image';

/** A picked file waiting to ride the next send. */
export interface StagedFile {
  id: string;
  file: File;
  name: string;
  kind: ThreadFileKind;
  /**
   * The type the server's policy stores this extension as — never the
   * browser-reported `file.type`, which is often empty (Office files).
   */
  contentType: string;
  /** Local object URL for an image's thumbnail (see `objectUrl` for who revokes it). */
  previewUrl?: string;
}

export interface PickResult {
  accepted: StagedFile[];
  refusals: string[];
}

/** What a pick is checked against. */
export interface PickContext {
  policy: ThreadFilePolicy;
  /** Files already staged on the message — they count toward its cap. */
  alreadyStaged: number;
  newId: () => string;
}

type FileCheck = { ok: true; type: ThreadFilePolicyType } | { ok: false; refusal: string };

/** The largest pick of this kind: an image may run over the per-file limit, since its compressed copy is what uploads. */
function pickSizeLimit(kind: ThreadFileKind, policy: ThreadFilePolicy): number {
  if (kind === COMPRESSIBLE_KIND) return policy.max_file_bytes * COMPRESSIBLE_PICK_SIZE_FACTOR;
  return policy.max_file_bytes;
}

/**
 * Type before size: a file we can never attach is refused as such, rather
 * than told it's too large as though a smaller copy would do. An empty file
 * has no bytes for the server to check, so it never gets that far.
 */
function checkFile(file: File, policy: ThreadFilePolicy): FileCheck {
  const type = threadFileTypeFor(policy, file.name);
  if (!type) return { ok: false, refusal: FILE_TYPE_REFUSED_MESSAGE(file.name) };
  if (file.size === 0) return { ok: false, refusal: FILE_EMPTY_MESSAGE(file.name) };
  const maxBytes = pickSizeLimit(type.kind, policy);
  if (file.size > maxBytes) return { ok: false, refusal: FILE_TOO_LARGE_MESSAGE(file.name, maxBytes) };
  return { ok: true, type };
}

/** A moment's frame isn't a file here, so it never counts toward the cap. */
function messageHasRoom(stagedCount: number, policy: ThreadFilePolicy): boolean {
  return stagedCount < policy.max_files_per_message;
}

function stageFile(file: File, type: ThreadFilePolicyType, id: string): StagedFile {
  const staged: StagedFile = { id, file, name: file.name, kind: type.kind, contentType: type.content_type };
  if (type.kind !== PREVIEWABLE_KIND) return staged;
  return { ...staged, previewUrl: createObjectUrl(file) };
}

/**
 * Splits a pick into the files to stage and one refusal per rejected file.
 * Files past the per-message cap (counting `alreadyStaged`) are dropped with
 * a single `TOO_MANY_FILES_MESSAGE`, and get no preview URL.
 */
export function validatePicks(picked: File[], { policy, alreadyStaged, newId }: PickContext): PickResult {
  const accepted: StagedFile[] = [];
  const refusals: string[] = [];
  let overCap = false;

  for (const file of picked) {
    const check = checkFile(file, policy);
    if (!check.ok) {
      refusals.push(check.refusal);
      continue;
    }
    if (!messageHasRoom(alreadyStaged + accepted.length, policy)) {
      overCap = true;
      continue;
    }
    accepted.push(stageFile(file, check.type, newId()));
  }

  if (overCap) refusals.push(TOO_MANY_FILES_MESSAGE(policy.max_files_per_message));
  return { accepted, refusals };
}
